import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, scan } from 'rxjs/operators';

import {
  MeetingState,
  MeetingStateEvent,
  createInitialMeetingState,
  reduceMeetingState
} from '@models/MeetingState';
import { OpentokService } from './opentok.service';
import { RoomSessionService } from './room-session.service';

export class MeetingCommandError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'MeetingCommandError';
  }
}

/**
 * Single UI-facing coordinator for meeting media commands and state.
 *
 * OpentokService remains the backwards-compatible transport adapter. This
 * coordinator serializes user intents and reduces provider callbacks into one
 * immutable snapshot, without changing the current translation REST/WS API.
 */
@Injectable({ providedIn: 'root' })
export class MeetingCoordinatorService implements OnDestroy {
  private readonly events = new Subject<MeetingStateEvent>();
  private readonly stateSource = new BehaviorSubject<MeetingState>(createInitialMeetingState());
  private readonly subscriptions = new Subscription();

  private operationSequence = 0;
  private screenQueue: Promise<void> = Promise.resolve();
  private translationQueue: Promise<void> = Promise.resolve();
  private cameraQueue: Promise<void> = Promise.resolve();

  readonly state$: Observable<MeetingState> = this.stateSource.asObservable()
    .pipe(distinctUntilChanged());

  constructor(
    private readonly opentokService: OpentokService,
    private readonly roomSessionService: RoomSessionService
  ) {
    this.subscriptions.add(
      this.events.pipe(scan(reduceMeetingState, createInitialMeetingState()))
        .subscribe(state => this.stateSource.next(state))
    );

    this.subscriptions.add(this.roomSessionService.currentRoom.subscribe(() => this.observeContext()));
    this.subscriptions.add(this.roomSessionService.sessionActive.subscribe(active => {
      this.observeContext();
      this.dispatch({ type: 'SESSION_ACTIVE_OBSERVED', active });
    }));

    this.subscriptions.add(this.opentokService.currentPublisher.subscribe(publisher => {
      this.dispatch({
        type: 'CAMERA_OBSERVED',
        streamId: publisher?.stream?.streamId || null,
        hasAudio: !!publisher?.stream?.hasAudio,
        hasVideo: !!publisher?.stream?.hasVideo
      });
    }));
    this.subscriptions.add(this.opentokService.publishAudio.subscribe(enabled => {
      this.dispatch({ type: 'CAMERA_AUDIO_OBSERVED', enabled });
    }));
    this.subscriptions.add(this.opentokService.publishVideo.subscribe(enabled => {
      this.dispatch({ type: 'CAMERA_VIDEO_OBSERVED', enabled });
    }));
    this.subscriptions.add(this.opentokService.screenPublishState.subscribe(active => {
      this.dispatch({ type: 'SCREEN_AVAILABILITY_OBSERVED', available: active !== null });
      if (active !== null) {
        this.dispatch({
          type: 'SCREEN_OBSERVED',
          active,
          streamId: active ? this.opentokService.currentScreenPublisher?.stream?.streamId || null : null
        });
      }
    }));
    this.subscriptions.add(this.opentokService.rtranslationPublisher.subscribe(publisher => {
      this.dispatch({
        type: 'TRANSLATION_OBSERVED',
        active: !!publisher,
        streamId: publisher?.stream?.streamId || null
      });
    }));
  }

  get snapshot(): MeetingState {
    return this.stateSource.value;
  }

  setScreenSharing(desired: boolean): Promise<void> {
    const operationId = this.nextOperationId();
    this.dispatch({ type: 'SCREEN_COMMAND_STARTED', desired, operationId });

    const operation = this.screenQueue.then(async () => {
      if (!this.isCurrentScreenOperation(operationId)) {
        return;
      }
      if (desired && this.snapshot.context.phase !== 'connected') {
        throw new MeetingCommandError('SESSION_NOT_CONNECTED', 'Screen sharing requires an active meeting.');
      }
      if (desired && !this.snapshot.screen.available) {
        throw new MeetingCommandError('SCREEN_SHARE_NOT_AVAILABLE', 'Screen sharing is not available for this participant.');
      }

      if (desired) {
        const existing = this.opentokService.currentScreenPublisher;
        const publisher = existing?.stream ? existing : await this.opentokService.startScreenSharing();
        if (!publisher?.stream?.streamId) {
          throw new MeetingCommandError('SCREEN_PUBLISH_FAILED', 'OpenTok did not create a screen stream.');
        }
        if (this.isCurrentScreenOperation(operationId)) {
          this.dispatch({
            type: 'SCREEN_COMMAND_SUCCEEDED',
            desired: true,
            operationId,
            streamId: publisher.stream.streamId
          });
        }
      } else {
        this.opentokService.endScreenSharing();
        if (this.isCurrentScreenOperation(operationId)) {
          this.dispatch({
            type: 'SCREEN_COMMAND_SUCCEEDED',
            desired: false,
            operationId,
            streamId: null
          });
        }
      }
    }).catch(error => {
      if (this.isCurrentScreenOperation(operationId)) {
        this.dispatch({
          type: 'SCREEN_COMMAND_FAILED',
          desired,
          operationId,
          error: this.describeError(error)
        });
      }
      throw error;
    });

    this.screenQueue = operation.then(() => undefined, () => undefined);
    return operation;
  }

  startTranslation(from: string, to: string): Promise<void> {
    return this.setTranslation(true, from, to);
  }

  stopTranslation(): Promise<void> {
    return this.setTranslation(false);
  }

  /** Serialize rapid mic toggles so each toggle reads the last applied state. */
  toggleAudio(): Promise<void> {
    const operation = this.cameraQueue.then(() => this.opentokService.toggleAudio());
    this.cameraQueue = operation.then(() => undefined, () => undefined);
    return operation;
  }

  /** Serialize rapid camera toggles for the same reason as microphone toggles. */
  toggleVideo(): Promise<void> {
    const operation = this.cameraQueue.then(() => this.opentokService.toggleVideo());
    this.cameraQueue = operation.then(() => undefined, () => undefined);
    return operation;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.events.complete();
    this.stateSource.complete();
  }

  private setTranslation(desired: boolean, from?: string, to?: string): Promise<void> {
    const operationId = this.nextOperationId();
    const requestedSourceStreamId = desired ? this.opentokService.getActiveAudioStreamId() : null;
    this.dispatch({
      type: 'TRANSLATION_COMMAND_STARTED',
      desired,
      operationId,
      sourceStreamId: requestedSourceStreamId
    });

    const operation = this.translationQueue.then(async () => {
      if (!this.isCurrentTranslationOperation(operationId)) {
        return;
      }

      if (!desired) {
        this.opentokService.stopRTranslation();
        if (this.isCurrentTranslationOperation(operationId)) {
          this.dispatch({
            type: 'TRANSLATION_COMMAND_SUCCEEDED',
            desired: false,
            operationId,
            streamId: null
          });
        }
        return;
      }

      if (this.snapshot.context.phase !== 'connected') {
        throw new MeetingCommandError('SESSION_NOT_CONNECTED', 'Translation requires an active meeting.');
      }
      const sourceStreamId = this.opentokService.getActiveAudioStreamId();
      const sourceAvailable = this.opentokService.hasActiveAudioSource();
      if (!sourceStreamId || !sourceAvailable) {
        throw new MeetingCommandError('AUDIO_STREAM_NOT_AVAILABLE', 'Translation requires an active microphone publication.');
      }

      // Intentionally delegates to the existing REST/WebSocket implementation.
      const publisher = await this.opentokService.startRTranslation(sourceStreamId, from, to);
      if (!publisher?.stream?.streamId) {
        throw new MeetingCommandError('TRANSLATION_START_FAILED', 'Translation did not create an OpenTok publisher.');
      }
      if (this.isCurrentTranslationOperation(operationId)) {
        this.dispatch({
          type: 'TRANSLATION_COMMAND_SUCCEEDED',
          desired: true,
          operationId,
          streamId: publisher.stream.streamId
        });
      }
    }).catch(error => {
      if (this.isCurrentTranslationOperation(operationId)) {
        // Converge partial WS/AudioContext/publisher starts on the existing stop path.
        this.opentokService.stopRTranslation();
        this.dispatch({
          type: 'TRANSLATION_COMMAND_FAILED',
          desired,
          operationId,
          error: this.describeError(error)
        });
      }
      throw error;
    });

    this.translationQueue = operation.then(() => undefined, () => undefined);
    return operation;
  }

  private observeContext(): void {
    this.dispatch({
      type: 'CONTEXT_OBSERVED',
      roomId: this.roomSessionService.currentRoomId || null,
      meetingId: this.roomSessionService.currentSessionId || null
    });
  }

  private nextOperationId(): number {
    this.operationSequence += 1;
    return this.operationSequence;
  }

  private isCurrentScreenOperation(operationId: number): boolean {
    return this.snapshot.screen.operationId === operationId;
  }

  private isCurrentTranslationOperation(operationId: number): boolean {
    return this.snapshot.translation.operationId === operationId;
  }

  private describeError(error: any): string {
    return error?.code || error?.message || error?.name || String(error || 'unknown-error');
  }

  private dispatch(event: MeetingStateEvent): void {
    this.events.next(event);
  }
}
